import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { Lock, PauseCircle, Archive, Settings, Heart } from 'lucide-react';
import './styles.css';
import { useAuth0 } from "@auth0/auth0-react";
import LoadingSpinner from '../../components/LoadingSpinner';
import SkeletonLoader from '../../components/SkeletonLoader';
import { useCurrentUser, useFamilyMembers, useStorageUsage } from '../../hooks/useApi';
import { useSubscription } from '../../hooks/useSubscription';
import { ParticleDrift, ShimmeringLine } from '../../components/LivingSystemCues';
import sovereignCircleImg from '../../images/sovereign-circle.png';
import legacyImg from '../../images/legacy.png';

// VaultPasscode Component (inline for convenience)
function VaultPasscode({
  memberName,
  passcode,
  setPasscode,
  onSubmit,
  onClose,
  loading,
  error,
  navigate
}) {
  const inputRef = useRef(null);

  // Handle input - allow pasting and any numeric length (no limit)
  const handleInput = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setPasscode(val);
  };

  // Handle paste event to ensure full paste works
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const numericOnly = pastedText.replace(/[^0-9]/g, '');
    setPasscode(numericOnly);
    // Focus and move cursor to end after paste
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(numericOnly.length, numericOnly.length);
      }
    }, 0);
  };

  return (
    <div className="vault-passcode-overlay">
      <div className="vault-passcode-card" style={{ position: 'relative' }}>
        {/* Close button */}
        <button
          className="vault-passcode-close-btn"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'rgba(40, 35, 29, 0.9)',
            border: '2px solid #e2c89c',
            borderRadius: '50%',
            width: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#e2c89c',
            fontSize: '18px',
            fontWeight: 'bold',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#e2c89c';
            e.target.style.color = '#18120b';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(40, 35, 29, 0.9)';
            e.target.style.color = '#e2c89c';
          }}
        >
          ×
        </button>

        <div className="vault-passcode-header">
          <div className="vault-member-name">{memberName}'s Vault</div>
          <div className="vault-passcode-title">ENTER VAULT KEY</div>
        </div>
        <form className="vault-passcode-form" onSubmit={onSubmit} autoComplete="off">
          <div className="vault-passcode-input-container">
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              className="vault-passcode-input-single"
              value={passcode}
              onChange={handleInput}
              onPaste={handlePaste}
              autoFocus
              disabled={loading}
              placeholder="Enter your passcode"
              aria-label="Vault key passcode"
            />
          </div>
          <button
            className="vault-unlock-btn"
            type="submit"
            disabled={passcode.length < 4 || loading}
          >
            {loading ? 'VERIFYING...' : 'UNLOCK VAULT'}
          </button>
          {error && <div className="vault-error">{error}</div>}
          <div className="vault-forgot-passcode">
            <button
              type="button"
              className="forgot-passcode-link"
              onClick={() => navigate('/forgot-passcode')}
              style={{
                background: 'none',
                border: 'none',
                color: '#bfa97a',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginTop: '1rem'
              }}
            >
              Forgot your vault key?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status, statusReason }) {
  const reason = (statusReason || '').toLowerCase();
  if (status === 'legacy' && (reason === 'deceased' || reason === 'death')) {
    return <span className="badge legacy" title="Deceased — Legacy Mode"><Heart size={14} /> Legacy</span>;
  }
  if (status === 'legacy') return <span className="badge legacy" title="Incapacitated — Legacy Mode"><Lock size={14} /> Incapacitated</span>;
  if (status === 'inactive') return <span className="badge inactive" title="Inactive"><PauseCircle size={14} /> Inactive</span>;
  if (status === 'locked') return <span className="badge locked" title="Vault Locked"><Lock size={14} /> Locked</span>;
  if (status === 'custom') return <span className="badge custom" title={statusReason}>{statusReason}</span>;
  return null;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("MemberSelection ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', color: '#e5c97b', textAlign: 'center', background: '#0a0a0a', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <h2>Something went wrong loading this page.</h2>
          <p style={{ color: '#ff6b6b', margin: '1rem 0' }}>{this.state.error?.toString()}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 20px', background: '#bfa97a', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const MemberSelection = memo(function MemberSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMember, setSelectedMember] = useState(null);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [minDisplayTimeElapsed, setMinDisplayTimeElapsed] = useState(false);
  const containerRef = useRef(null);
  const { getAccessTokenSilently, user } = useAuth0();
  // Use same API_BASE_URL logic as useApi.js for consistency
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (process.env.NODE_ENV === 'development'
      ? 'http://localhost:5001'
      : 'https://api.eternavaults.com');
  const [partnerInfo, setPartnerInfo] = useState(null);

  // Use React Query hooks for data fetching
  const { data: currentMember, isLoading: userLoading, error: userError } = useCurrentUser();

  // Fetch partner info if member has partnerId
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!currentMember?.partnerId) {
        setPartnerInfo(null);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const memberRes = await fetch(`${API_BASE_URL}/api/members/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (memberRes.ok) {
          const member = await memberRes.json();
          if (member.partnerBranding || member.partnerName) {
            setPartnerInfo({
              name: member.partnerName || member.partnerCompany || 'Your Vault',
              logo: member.partnerBranding?.logoUrl || member.partnerBranding?.logo
            });
          }
        }
      } catch (error) {
        console.error('Error fetching partner info:', error);
      }
    };

    if (currentMember) {
      fetchPartnerInfo();
    }
  }, [currentMember, getAccessTokenSilently]);
  const { data: familyData, isLoading: familyLoading, error: familyError, refetch: refetchFamilyMembers } = useFamilyMembers();
  const { data: planInfo, isLoading: planLoading, error: planError } = useStorageUsage();
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscription();

  // Refetch family members when landing on this page so added members show up (avoids stale cache)
  useEffect(() => {
    refetchFamilyMembers();
  }, [refetchFamilyMembers]);

  // Ensure vault animation displays for minimum 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinDisplayTimeElapsed(true);
    }, 0.4); // 2.5 seconds minimum display time
    return () => clearTimeout(timer);
  }, []);

  // Set initial loading to false once hooks have initialized AND minimum display time has elapsed
  useEffect(() => {
    // Only turn off loading if:
    // 1. Minimum display time has elapsed (2.5 seconds)
    // 2. Hooks have initialized
    // 3. All hooks have finished loading
    if (minDisplayTimeElapsed) {
      const hooksInitialized = userLoading !== undefined || familyLoading !== undefined || planLoading !== undefined || subscriptionLoading !== undefined;
      if (hooksInitialized) {
        const timer = setTimeout(() => {
          setInitialLoading(false);
        }, 0.4); // Small delay for smooth transition
        return () => clearTimeout(timer);
      }
    }
  }, [minDisplayTimeElapsed, userLoading, familyLoading, planLoading, subscriptionLoading]);

  // Extract data from family response
  const members = familyData?.members || [];
  const isAdmin = currentMember?.isAdmin || false;

  // Clear sessionStorage when returning to member selection
  // This ensures clean state when switching between vaults
  useEffect(() => {
    sessionStorage.removeItem('currentMember');
    sessionStorage.removeItem('passcodeVerifiedAt');
  }, []);

  // Handle payment success notification
  useEffect(() => {
    if (location.state?.paymentSuccess) {
      setShowPaymentSuccess(true);
      // Auto-hide after 5 seconds
      setTimeout(() => setShowPaymentSuccess(false), 5000);
    }
  }, [location.state]);

  // Handle "Start New Family" navigation based on subscription status
  const handleStartNewFamily = () => {
    if (hasActiveSubscription) {
      // User has active subscription, go directly to create family
      navigate('/create-family');
    } else {
      // User needs to choose a plan first
      navigate('/choose-plan');
    }
  };

  // Determine loading and error states
  // Show skeleton if:
  // 1. initialLoading is true (component just mounted)
  // 2. OR any hook is actively loading
  // 3. OR hooks haven't initialized yet (all undefined)
  const hooksInitialized = userLoading !== undefined || familyLoading !== undefined || planLoading !== undefined || subscriptionLoading !== undefined;
  const anyHookLoading = userLoading === true || familyLoading === true || planLoading === true || subscriptionLoading === true;
  const isLoading = initialLoading || anyHookLoading || !hooksInitialized;
  const hasError = userError || familyError || planError;

  // Debug logging
  useEffect(() => {
    console.log('🔵 MemberSelection state:', {
      isLoading,
      initialLoading,
      anyHookLoading,
      hooksInitialized,
      hasError,
      userError: userError?.message,
      familyError: familyError?.message,
      planError: planError?.message,
      currentMember: !!currentMember,
      membersCount: members.length,
      API_BASE_URL
    });
  }, [isLoading, initialLoading, anyHookLoading, hooksInitialized, hasError, userError, familyError, planError, currentMember, members.length, API_BASE_URL]);

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setPasscode('');
    setPasscodeError('');
  };

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPasscodeError('');
    if (!selectedMember?._id) {
      setPasscodeError('No member selected');
      setLoading(false);
      return;
    }

    try {
      // Get Auth0 token
      const token = await getAccessTokenSilently({
        audience: "https://api.eternavaults.com"
      });
      // 1. Verify passcode (now with JWT)
      const verifyRes = await fetch(`${API_BASE_URL}/api/members/${selectedMember._id}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ passcode }),
      });

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json();
        throw new Error(errorData.error || 'Invalid vault key. Please try again.');
      }

      // 2. Fetch dashboard data with valid token
      const dashboardRes = await fetch(`${API_BASE_URL}/api/dashboard/${selectedMember._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!dashboardRes.ok) throw new Error('Vault access denied. Please verify your vault key.');
      // 3. Store session data and navigate only after successful verification AND dashboard access
      sessionStorage.setItem('currentMember', JSON.stringify(selectedMember));
      sessionStorage.setItem('passcodeVerifiedAt', Date.now().toString());
      navigate(`/dashboard/${selectedMember._id}`);
    } catch (err) {
      setPasscodeError(err.message || 'We couldn\'t access your vault. Please verify your vault key and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    // If no members exist, redirect to choose plan first
    if (members.length === 0) {
      navigate('/choose-plan');
    } else {
      // If members exist, go to account management to add more
      navigate('/member-selection');
    }
  };

  // Handle the case when user has no family/members
  useEffect(() => {
    // If no members found and not loading, check subscription status
    if (!isLoading && members.length === 0 && !hasError) {
      if (hasActiveSubscription) {
        // User has subscription but no family, redirect to create family
        navigate('/create-family');
      } else {
        // User has no subscription, redirect to choose plan
        navigate('/choose-plan');
      }
      return;
    }

    if (!isLoading && containerRef.current) {
      gsap.from(containerRef.current, {
        y: 20,
        duration: 0.4,
        ease: "power3.out"
      });
    }
  }, [isLoading, members.length, hasError, navigate]);

  console.log('🔵 MemberSelection render check:', {
    isLoading,
    initialLoading,
    anyHookLoading,
    hooksInitialized,
    hasError,
    userLoading,
    familyLoading,
    planLoading,
    subscriptionLoading
  });

  if (isLoading) {
    return (
      <div
        className="vault-loading-container"
        style={{
          minHeight: '100vh',
          width: '100%',
          background: '#0a0a0a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999
        }}
      >
        <div className="vault-loading-bg" style={{ width: '100%', height: '100%' }}>
          {/* Animated Particles */}
          <div className="vault-loading-particle vault-loading-particle--1"></div>
          <div className="vault-loading-particle vault-loading-particle--2"></div>
          <div className="vault-loading-particle vault-loading-particle--3"></div>
          <div className="vault-loading-particle vault-loading-particle--4"></div>
          <div className="vault-loading-particle vault-loading-particle--5"></div>
          <div className="vault-loading-particle vault-loading-particle--6"></div>

          {/* Vault Door */}
          <div className="vault-loading-door">
            <svg width="200" height="240" viewBox="0 0 200 240" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Door Frame */}
              <rect x="20" y="30" width="160" height="180" rx="8" stroke="#C8AA6E" strokeWidth="3" fill="none" opacity="0.4" className="vault-door-frame" />

              {/* Door */}
              <rect x="30" y="40" width="140" height="160" rx="6" fill="url(#vaultDoorGradient)" className="vault-door-main" />

              {/* Lock Mechanism */}
              <circle cx="100" cy="120" r="20" stroke="#C8AA6E" strokeWidth="2.5" fill="none" opacity="0.7" className="vault-lock-ring" />
              <circle cx="100" cy="120" r="12" stroke="#E5C97B" strokeWidth="2" fill="none" opacity="0.9" className="vault-lock-core" />
              <path d="M100 108 L100 112" stroke="#E5C97B" strokeWidth="2.5" strokeLinecap="round" className="vault-keyhole" />

              {/* Decorative Elements */}
              <path d="M50 60 L80 60 M120 60 L150 60" stroke="#C8AA6E" strokeWidth="2" opacity="0.3" className="vault-decoration" />
              <path d="M50 200 L80 200 M120 200 L150 200" stroke="#C8AA6E" strokeWidth="2" opacity="0.3" className="vault-decoration" />

              {/* Gradient Definitions */}
              <defs>
                <linearGradient id="vaultDoorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#2A231B" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="#1A1612" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="#0F0B08" stopOpacity="1" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Loading Text */}
          <div className="vault-loading-text">
            <h2 className="vault-loading-title">Accessing Vault</h2>
            <p className="vault-loading-subtitle">Unlocking your family legacy...</p>
          </div>

          {/* Progress Indicator */}
          <div className="vault-loading-progress">
            <div className="vault-progress-bar">
              <div className="vault-progress-fill"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div ref={containerRef}>
        {/* Payment Success Notification */}
        {showPaymentSuccess && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(135deg, #4CAF50, #45a049)',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <div style={{ fontSize: '24px' }}>🎉</div>
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Payment Successful!</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Welcome to {partnerInfo?.name || 'EternaVaults'}! Please select a family member to continue.
              </div>
            </div>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                marginLeft: '8px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="vaults-grid">
          <div className="members-list">
            <div className="vault-selection-bg">
              <div className="vault-selection-container">
                {partnerInfo?.logo ? (
                  <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    <img
                      src={partnerInfo.logo}
                      alt={partnerInfo.name}
                      style={{
                        maxHeight: '60px',
                        maxWidth: '200px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                ) : (
                  <h1 className="member-title">
                    {partnerInfo?.name || 'ETERNA VAULTS'}
                  </h1>
                )}
                <p className="member-subtitle">Select Your Vault Identity</p>
                {members.length > 0 && (
                  <p className="member-instructions">
                    Choose a vault identity to continue
                  </p>
                )}
                {hasError ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '180px',
                    gap: '16px'
                  }}>
                    <div style={{ color: '#f44336', textAlign: 'center', fontWeight: '600', marginBottom: '8px' }}>
                      Connection Error
                    </div>
                    <div style={{ color: '#bfa97a', textAlign: 'center', fontSize: '14px', marginBottom: '8px' }}>
                      {userError?.message || familyError?.message || planError?.message || 'Unable to connect to the server. Please check your connection and try again.'}
                    </div>
                    {process.env.NODE_ENV === 'development' && (
                      <div style={{ color: '#8a7a5a', textAlign: 'center', fontSize: '12px', marginBottom: '8px' }}>
                        API: {API_BASE_URL}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => {
                          window.location.reload();
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#bfa97a',
                          color: '#18120b',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Retry
                      </button>
                      <button
                        onClick={handleStartNewFamily}
                        style={{
                          padding: '8px 16px',
                          background: '#e5c97b',
                          color: '#18120b',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Start New Family
                      </button>
                    </div>
                  </div>
                ) : loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '180px' }}>
                    <LoadingSpinner message="Accessing your vaults..." />
                  </div>
                ) : (
                  <div className="vaults-grid">
                    {members.length === 0 ? (
                      <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#7a6a3a'
                      }}>
                        <div style={{ fontSize: '1.2rem', marginBottom: '16px' }}>
                          Welcome to {partnerInfo?.name || 'Eterna Vaults'}!
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                          You haven't created your family vault yet. Let's get started!
                        </div>
                        <button
                          onClick={handleStartNewFamily}
                          style={{
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #e5c97b 0%, #bfa97a 100%)',
                            color: '#18120b',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'normal',
                            boxShadow: '0 2px 8px rgba(191, 169, 122, 0.3)'
                          }}
                        >
                          Create Your Family Vault
                        </button>
                      </div>
                    ) : (
                      <>
                        {Array.isArray(members) && members.filter(m => m && m._id).map((member, index) => {
                          // Generate unique identity marker (monogram initial)
                          const memberName = member.name || 'Unknown Vault';
                          const initial = (memberName.charAt(0) || '?').toUpperCase();
                          const roleContext = member.isAdmin ? 'Family Admin' : 'Personal Vault';

                          return (
                            <div
                              key={member._id}
                              className={`vault-square${selectedMember && selectedMember._id === member._id ? ' selected' : ''}`}
                              onClick={() => handleSelectMember(member)}
                              tabIndex={0}
                              role="button"
                              data-member-index={index}
                            >
                              {/* Identity Marker (Monogram) */}
                              <div className="vault-identity-marker" data-initial={initial}>
                                {initial}
                              </div>

                              {/* Icon above */}
                              <div className="vault-square-icon">
                                <Archive size={36} />
                              </div>

                              {/* Name - centered, prominent */}
                              <div className="vault-square-name">
                                <div className="vault-name-primary">{memberName}</div>
                                <div className="vault-name-context">{roleContext}</div>
                                {planInfo?.plan === "Sovereign Circle (Beta Testers)" && (
                                  <img
                                    src={sovereignCircleImg}
                                    alt="Sovereign Circle Beta Tester"
                                    className="vault-plan-badge"
                                    title="Sovereign Circle Beta Tester"
                                  />
                                )}
                                {planInfo?.plan === "Legacy" && (
                                  <img
                                    src={legacyImg}
                                    alt="Legacy Plan"
                                    className="vault-plan-badge"
                                    title="Legacy Plan"
                                  />
                                )}
                                <StatusBadge status={member.status} statusReason={member.statusReason} />
                              </div>

                              {/* Role indicator (replaces crown) */}
                              {member.isAdmin && (
                                <div className="vault-role-badge" title="Administrator">
                                  Principal
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {selectedMember && (
          <VaultPasscode
            memberName={selectedMember.name || 'Unknown Member'}
            passcode={passcode}
            setPasscode={setPasscode}
            onSubmit={handlePasscodeSubmit}
            onClose={() => setSelectedMember(null)}
            loading={loading}
            error={passcodeError}
            navigate={navigate}
          />
        )}
      </div>
    </ErrorBoundary>
  );
});

export default MemberSelection;
